// Pure wizard helpers shared by the questionnaire stepper. Kept free of React
// so the step grouping and the answer-merge that drives the "Continue" gate can
// be unit-tested directly (the app has no DOM test environment).

import type { Json, Question, QuestionCategory, Submission } from './types.ts';

export type Step =
  | { kind: 'company-info' }
  | { kind: 'questions'; category: QuestionCategory; questions: Question[] };

// Synthetic bucket for visible questions whose category_id has no matching
// category row. Without this they would be counted toward progress yet rendered
// in no step at all, which silently dead-ends "Submit".
const ORPHAN_CATEGORY: QuestionCategory = {
  id: '__unsorted__',
  name: 'Additional questions',
  questionnaire_ids: [],
  display_order: Number.MAX_SAFE_INTEGER,
  created_at: '',
  updated_at: '',
};

/**
 * Re-seat per-option mirror questions (instance_of != null) immediately after
 * their authored row, ordered by the parent multiselect's option order.
 * Mirrors share their authored row's content, so rendering them adjacent reads
 * as "the same question, once per option" instead of scattering by
 * display_order. Mirrors whose authored row is absent from the list stay where
 * they are. Returns the input array itself when there is nothing to re-seat.
 */
export function orderWithInstances(questions: Question[]): Question[] {
  const present = new Set(questions.map((q) => q.id));
  const mirrorsByAuthored = new Map<string, Question[]>();
  for (const q of questions) {
    if (!q.instance_of || !present.has(q.instance_of)) continue;
    const list = mirrorsByAuthored.get(q.instance_of) ?? [];
    list.push(q);
    mirrorsByAuthored.set(q.instance_of, list);
  }
  if (mirrorsByAuthored.size === 0) return questions;

  // NOTE: instance_of always points at a non-mirror authored row (the API's
  // option-links endpoint rejects setting instance_of on anything that itself
  // has instance_of set), so cycles/self-references cannot occur here. A
  // hypothetical A↔B cycle would drop both rows; the API prevents it upstream.
  const byId = new Map(questions.map((q) => [q.id, q]));
  function optionIndex(q: Question): number {
    const parent = q.parent_id ? byId.get(q.parent_id) : undefined;
    const value = q.condition?.value;
    if (!parent?.options || typeof value !== 'string') return Number.MAX_SAFE_INTEGER;
    const idx = parent.options.indexOf(value);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  }

  const out: Question[] = [];
  for (const q of questions) {
    if (q.instance_of && present.has(q.instance_of)) continue; // re-seated below
    out.push(q);
    const group = mirrorsByAuthored.get(q.id);
    if (group) out.push(...[...group].sort((a, b) => optionIndex(a) - optionIndex(b)));
  }
  return out;
}

/**
 * Group the visible questions into wizard steps: an optional leading
 * company-info step, then one step per category (in category order) that has at
 * least one visible question, preserving each question's input order.
 *
 * Any visible question whose category_id is not in `categories` is collected
 * into a single trailing "Additional questions" step so that every visible
 * question is always rendered somewhere — a required question can never be
 * counted toward completion while being impossible to reach.
 */
export function buildSteps(
  questions: Question[],
  categories: QuestionCategory[],
  visibleIds: Set<string>,
  includeCompanyInfoStep: boolean,
): Step[] {
  const steps: Step[] = [];
  if (includeCompanyInfoStep) steps.push({ kind: 'company-info' });

  const known = new Set(categories.map((c) => c.id));
  const groups = new Map<string, Question[]>();
  for (const c of categories) groups.set(c.id, []);

  const orphans: Question[] = [];
  for (const question of orderWithInstances(questions)) {
    if (!visibleIds.has(question.id)) continue;
    if (known.has(question.category_id)) {
      groups.get(question.category_id)!.push(question);
    } else {
      orphans.push(question);
    }
  }

  for (const category of categories) {
    const qs = groups.get(category.id)!;
    if (qs.length > 0) steps.push({ kind: 'questions', category, questions: qs });
  }
  if (orphans.length > 0) {
    steps.push({ kind: 'questions', category: ORPHAN_CATEGORY, questions: orphans });
  }

  return steps;
}

export type LocalAnswer = { answer: Json | null; comment: string | null };

function syntheticSubmission(questionId: string, local: LocalAnswer): Submission {
  return {
    id: `local-${questionId}`,
    company_id: '',
    question_id: questionId,
    answer: local.answer,
    comment: local.comment,
    status: 'pending',
    reviewer_id: null,
    reviewer_comment: null,
    reviewed_at: null,
    reference_doc_name: null,
    reference_doc_path: null,
    created_at: '',
    updated_at: '',
    submission_files: [],
    submission_adoptions: [],
  };
}

/**
 * Overlay the answers the company has entered this session on top of the
 * server-fetched submissions, so the completion/step gate reflects what the
 * user actually typed/selected the moment they do it — not only after the save
 * round-trips back. Files and adoptions (which are tracked server-side) are
 * preserved on any existing submission; questions with no submission yet get a
 * synthetic pending row carrying just the local answer.
 */
export function applyLocalAnswers(
  submissions: Submission[],
  localAnswers: Map<string, LocalAnswer>,
): Submission[] {
  if (localAnswers.size === 0) return submissions;

  const seen = new Set<string>();
  const merged = submissions.map((s) => {
    const local = localAnswers.get(s.question_id);
    if (!local) return s;
    seen.add(s.question_id);
    return { ...s, answer: local.answer, comment: local.comment };
  });

  for (const [questionId, local] of localAnswers) {
    if (!seen.has(questionId)) merged.push(syntheticSubmission(questionId, local));
  }

  return merged;
}
